import {
  Alert,
  Button,
  Card,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useHref, useLinkClickHandler } from 'react-router-dom';
import { useAuthenticatedRequest } from '../../UserContext';
import {
  ConferenceApiError,
  conferenceListRequest,
  ConferenceReport,
  ReportPage,
} from './api';
import { DeleteReportAction } from './delete-report';

const statuses: Record<string, string | undefined> = {
  all: undefined,
  active: 'preparing,queued,running',
  completed: 'completed',
  failed: 'failed',
};

/** Navigate the server's opaque cursors without assuming a total row count. */
export function ReportRuns({
  apiUrl,
  onDeleted,
  onUnavailable,
}: {
  apiUrl: string;
  onDeleted: () => void;
  onUnavailable: () => void;
}): JSX.Element {
  const [filter, setFilter] = useState('all');
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  return (
    <Card>
      <Radio.Group
        aria-label="Report status filter"
        onChange={event => {
          setFilter(event.target.value as string);
          setCursors([]);
        }}
        options={[
          { label: 'All', value: 'all' },
          { label: 'Active', value: 'active' },
          { label: 'Completed', value: 'completed' },
          { label: 'Failed', value: 'failed' },
        ]}
        optionType="button"
        value={filter}
      />
      <ReportListPage
        apiUrl={apiUrl}
        cursor={cursor}
        key={`${filter}:${cursor ?? ''}`}
        onDeleted={onDeleted}
        onNext={next => setCursors(previous => [...previous, next])}
        onPrevious={() => setCursors(previous => previous.slice(0, -1))}
        onUnavailable={onUnavailable}
        pageNumber={cursors.length + 1}
        status={statuses[filter]}
      />
    </Card>
  );
}

/** Poll a visible page serially and discard responses after refresh or navigation. */
function ReportListPage({
  apiUrl,
  cursor,
  status,
  pageNumber,
  onNext,
  onPrevious,
  onDeleted,
  onUnavailable,
}: {
  apiUrl: string;
  cursor?: string;
  status?: string;
  pageNumber: number;
  onNext: (cursor: string) => void;
  onPrevious: () => void;
  onDeleted: () => void;
  onUnavailable: () => void;
}): JSX.Element {
  const authenticatedRequest = useAuthenticatedRequest();
  const request = useRef(authenticatedRequest);
  request.current = authenticatedRequest;
  const [page, setPage] = useState<ReportPage>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<number>();
  const [revision, setRevision] = useState(0);
  const pending = useRef<AbortController>();

  const refresh = () => {
    pending.current?.abort();
    setRevision(value => value + 1);
  };

  useEffect(() => {
    const controller = new AbortController();
    pending.current = controller;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      setLoading(true);
      let delay: number | undefined = 20_000;
      try {
        const result = await conferenceListRequest(
          apiUrl,
          request.current,
          controller.signal,
          cursor,
          status,
        );
        if (controller.signal.aborted) {
          return;
        }
        setPage(result);
        setRefreshedAt(Date.now());
        setError(undefined);
      } catch (error: unknown) {
        if (controller.signal.aborted) {
          return;
        }
        const failure =
          error instanceof ConferenceApiError
            ? error
            : new ConferenceApiError(0, 'LIST');
        setError(failure.message);
        if ([401, 403, 404, 405, 413, 422, 501].includes(failure.status)) {
          delay = undefined;
        } else {
          delay = Math.max(20_000, failure.retryAfterMs ?? 0);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
      if (!controller.signal.aborted && delay !== undefined) {
        timer = setTimeout(() => void poll(), delay);
      }
    };
    void poll();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [apiUrl, cursor, status, revision]);

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%', marginTop: 20 }}
    >
      <Space wrap>
        <Button disabled={loading} onClick={refresh}>
          Refresh
        </Button>
        <Typography.Text type="secondary">
          {refreshedAt !== undefined
            ? `Last refreshed: ${new Date(refreshedAt).toLocaleString()}`
            : 'No successful refresh yet.'}
        </Typography.Text>
        {loading && (
          <span role="status">
            {page ? 'Refreshing report runs…' : 'Loading report runs…'}
          </span>
        )}
      </Space>
      {error && (
        <Alert
          description={error}
          message={
            page
              ? 'Report runs may be stale. Showing the last successful list.'
              : 'Report runs unavailable'
          }
          showIcon
          type={page ? 'warning' : 'error'}
        />
      )}
      {page && (
        <>
          <Table<ConferenceReport>
            columns={[
              {
                title: 'Customer',
                dataIndex: 'email',
                render: (_, report) => (
                  <Space direction="vertical" size={0}>
                    <Typography.Text strong>
                      {report.client_name?.trim() ||
                        report.email ||
                        report.report_id}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      {report.email}
                    </Typography.Text>
                  </Space>
                ),
              },
              {
                title: 'Report ID',
                dataIndex: 'report_id',
                render: (id: string) => (
                  <Typography.Text copyable={{ text: id }}>
                    {id}
                  </Typography.Text>
                ),
              },
              {
                title: 'Status',
                dataIndex: 'status',
                render: (value: string) => <Tag>{value}</Tag>,
              },
              {
                title: 'Email delivery',
                dataIndex: 'delivery',
                render: (value: string | null) => value || 'Not yet confirmed',
              },
              {
                title: 'Submitted',
                dataIndex: 'submitted_at',
                render: formatTime,
              },
              {
                title: 'Last updated',
                dataIndex: 'updated_at',
                render: formatTime,
              },
              {
                title: 'Actions',
                render: (_, report) => (
                  <Space align="start" direction="vertical">
                    <ViewReportLink reportId={report.report_id} />
                    <DeleteReportAction
                      apiUrl={apiUrl}
                      onDeleted={() => {
                        setPage(
                          previous =>
                            previous && {
                              ...previous,
                              items: previous.items.filter(
                                item => item.report_id !== report.report_id,
                              ),
                            },
                        );
                        refresh();
                        onDeleted();
                      }}
                      onUnavailable={() => {
                        refresh();
                        onUnavailable();
                      }}
                      report={report}
                    />
                  </Space>
                ),
              },
            ]}
            dataSource={page.items}
            locale={{
              emptyText:
                pageNumber > 1
                  ? 'No reports on this page. Use Previous page to view newer reports.'
                  : status
                    ? 'No reports match this filter.'
                    : 'No reports to show.',
            }}
            pagination={false}
            rowKey="report_id"
            scroll={{ x: 1100 }}
          />
          <Space>
            <Button disabled={pageNumber === 1 || loading} onClick={onPrevious}>
              Previous page
            </Button>
            <Typography.Text>Page {pageNumber}</Typography.Text>
            <Button
              disabled={!page.next_cursor || loading}
              onClick={() => page.next_cursor && onNext(page.next_cursor)}
            >
              Next page
            </Button>
          </Space>
        </>
      )}
    </Space>
  );
}

/** Keep primary report navigation compatible with client routing and new tabs. */
function ViewReportLink({ reportId }: { reportId: string }): JSX.Element {
  const to = `?tab=runs&reportId=${reportId}`;
  const href = useHref(to);
  const onClick = useLinkClickHandler<HTMLAnchorElement>(to);
  return (
    <Button href={href} onClick={onClick} type="primary">
      View
    </Button>
  );
}

/** Display timestamps only when the backend supplied a valid date. */
function formatTime(value: string): string {
  const time = Date.parse(value);
  return Number.isFinite(time)
    ? new Date(time).toLocaleString()
    : 'Unavailable';
}
